import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';

type AgentTransferAgent = {
  associate_id: string;
  full_name: string | null;
  email: string | null;
  mobile_number: string | null;
  image_url: string | null;
  market_center_name: string | null;
  source_market_center_id: string | null;
  active_listing_count: string;
};

type AgentTransferMarketCenter = {
  id?: string | null;
  name: string;
  source_market_center_id: string;
};

type AgentTransferPortalResult = {
  portal: string;
  publishOk: boolean | null;
  publishError: string | null;
};

type AgentTransferListingResult = {
  listingId: string;
  listingNumber: string | null;
  address: string | null;
  portals: AgentTransferPortalResult[];
  error: string | null;
};

type AgentTransferJob = {
  id: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  associateId: string;
  associateName: string;
  fromMarketCenterSourceId: string;
  fromMarketCenterName: string | null;
  toMarketCenterSourceId: string;
  toMarketCenterName: string | null;
  totalListings: number;
  completedListings: number;
  listingsUpdated: number;
  results: AgentTransferListingResult[];
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
  requestedBy: string;
};

type AgentTransferHistoryRow = {
  id: string;
  associate_id: string;
  associate_name: string | null;
  from_source_market_center_id: string | null;
  from_market_center_name: string | null;
  to_source_market_center_id: string | null;
  to_market_center_name: string | null;
  listings_updated: number;
  requested_by: string | null;
  transfer_error: string | null;
  transferred_at: string;
};

export default function AgentTransferTab() {
  const { token, activeContext, isRegionalAdmin } = useAuth();
  const [agents, setAgents] = useState<AgentTransferAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [agentSearch, setAgentSearch] = useState('');
  const [marketCenters, setMarketCenters] = useState<AgentTransferMarketCenter[]>([]);
  const [marketCentersLoading, setMarketCentersLoading] = useState(false);
  const [targetSearch, setTargetSearch] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [targetMcSourceId, setTargetMcSourceId] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeJob, setActiveJob] = useState<AgentTransferJob | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyRows, setHistoryRows] = useState<AgentTransferHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const jobPollRef = useRef<number | null>(null);

  const authHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (activeContext?.id) headers['x-active-context'] = activeContext.id;
    return headers;
  }, [token, activeContext]);

  useEffect(() => {
    if (!isRegionalAdmin) return;
    setAgentsLoading(true);
    setAgentsError(null);
    fetch('/api/agent-transfer/mc-agents/__all__', { headers: authHeaders() })
      .then((response) => response.json())
      .then((data: { agents?: AgentTransferAgent[]; error?: string }) => {
        if (data.error) throw new Error(data.error);
        setAgents(data.agents ?? []);
      })
      .catch((err: unknown) => setAgentsError(err instanceof Error ? err.message : String(err)))
      .finally(() => setAgentsLoading(false));
  }, [isRegionalAdmin, authHeaders]);

  useEffect(() => {
    if (!isRegionalAdmin) return;
    setMarketCentersLoading(true);
    fetch('/api/market-centers?limit=500&offset=0&status=Active', { headers: authHeaders() })
      .then((response) => response.json())
      .then((data: { items?: AgentTransferMarketCenter[] }) => setMarketCenters(data.items ?? []))
      .catch(() => setMarketCenters([]))
      .finally(() => setMarketCentersLoading(false));
  }, [isRegionalAdmin, authHeaders]);

  useEffect(() => () => {
    if (jobPollRef.current) {
      window.clearTimeout(jobPollRef.current);
    }
  }, []);

  const selectedAgent = agents.find((agent) => agent.associate_id === selectedAgentId) ?? null;
  const selectedTargetMc = marketCenters.find((mc) => mc.source_market_center_id === targetMcSourceId) ?? null;

  const visibleAgents = useMemo(() => {
    const query = agentSearch.trim().toLowerCase();
    if (!query) return agents;
    return agents.filter((agent) => [
      agent.full_name,
      agent.email,
      agent.mobile_number,
      agent.market_center_name,
      agent.source_market_center_id,
    ].some((value) => String(value ?? '').toLowerCase().includes(query)));
  }, [agents, agentSearch]);

  const visibleTargetMcs = useMemo(() => {
    const query = targetSearch.trim().toLowerCase();
    return marketCenters
      .filter((mc) => mc.source_market_center_id !== selectedAgent?.source_market_center_id)
      .filter((mc) => {
        if (!query) return true;
        return [mc.name, mc.source_market_center_id].some((value) => String(value ?? '').toLowerCase().includes(query));
      });
  }, [marketCenters, selectedAgent?.source_market_center_id, targetSearch]);

  const canTransfer = Boolean(
    selectedAgent
    && selectedTargetMc
    && selectedAgent.source_market_center_id !== selectedTargetMc.source_market_center_id,
  );

  const scheduleJobPoll = useCallback((jobId: string) => {
    if (jobPollRef.current) window.clearTimeout(jobPollRef.current);
    jobPollRef.current = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/agent-transfer/jobs/${jobId}`, { headers: authHeaders() });
        const job = await response.json() as AgentTransferJob;
        setActiveJob(job);
        if (job.status === 'running' || job.status === 'pending') {
          scheduleJobPoll(jobId);
          return;
        }
        fetch('/api/agent-transfer/mc-agents/__all__', { headers: authHeaders() })
          .then((refreshResponse) => refreshResponse.json())
          .then((data: { agents?: AgentTransferAgent[] }) => {
            if (data.agents) setAgents(data.agents);
          })
          .catch(() => undefined);
      } catch {
        scheduleJobPoll(jobId);
      }
    }, 1500);
  }, [authHeaders]);

  async function startTransfer() {
    if (!selectedAgent || !selectedTargetMc) return;
    setShowConfirm(false);

    const response = await fetch('/api/agent-transfer/jobs', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        associateId: Number(selectedAgent.associate_id),
        toMarketCenterSourceId: selectedTargetMc.source_market_center_id,
        activeContext: activeContext?.id ?? null,
      }),
    });
    const data = await response.json() as { jobId?: string; error?: string };
    if (!response.ok || !data.jobId) {
      window.alert(`Failed to start transfer: ${data.error ?? 'Unknown error'}`);
      return;
    }

    setActiveJob({
      id: data.jobId,
      status: 'pending',
      associateId: selectedAgent.associate_id,
      associateName: selectedAgent.full_name ?? 'Unknown',
      fromMarketCenterSourceId: selectedAgent.source_market_center_id ?? '',
      fromMarketCenterName: selectedAgent.market_center_name,
      toMarketCenterSourceId: selectedTargetMc.source_market_center_id,
      toMarketCenterName: selectedTargetMc.name,
      totalListings: Number(selectedAgent.active_listing_count || 0),
      completedListings: 0,
      listingsUpdated: 0,
      results: [],
      startedAt: new Date().toISOString(),
      finishedAt: null,
      error: null,
      requestedBy: '',
    });
    setShowProgress(true);
    scheduleJobPoll(data.jobId);
  }

  function loadHistory() {
    setHistoryLoading(true);
    const params = selectedAgentId ? `?associateId=${encodeURIComponent(selectedAgentId)}` : '';
    fetch(`/api/agent-transfer/history${params}`, { headers: authHeaders() })
      .then((response) => response.json())
      .then((data: { log?: AgentTransferHistoryRow[] }) => setHistoryRows(data.log ?? []))
      .catch(() => setHistoryRows([]))
      .finally(() => setHistoryLoading(false));
  }

  if (!isRegionalAdmin) {
    return <div className="py-8 text-center text-sm text-slate-500">Agent Transfer is only available to Regional Admin users.</div>;
  }

  const progressPct = activeJob
    ? Math.round((activeJob.completedListings / Math.max(activeJob.totalListings, 1)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Agent Transfer</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Transfer an active agent to a new market centre. The associate profile and primary listings move to the new market centre while historical transactions remain unchanged.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setShowHistory(true); loadHistory(); }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:border-red-300 hover:text-red-700"
        >
          Transfer History
        </button>
      </div>

      <div className="surface-card space-y-4 p-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Step 1</p>
          <h3 className="mt-0.5 text-base font-semibold text-slate-800">Select source agent</h3>
        </div>

        <input
          value={agentSearch}
          onChange={(event) => setAgentSearch(event.target.value)}
          placeholder="Search agents by name, email, phone, or market centre..."
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-100"
        />

        {agentsLoading && <p className="text-sm text-slate-500">Loading agents...</p>}
        {agentsError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{agentsError}</div>}

        {!agentsLoading && !agentsError && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8">
            {visibleAgents.map((agent) => {
              const isSelected = selectedAgentId === agent.associate_id;
              return (
                <button
                  key={agent.associate_id}
                  type="button"
                  onClick={() => { setSelectedAgentId(agent.associate_id); setTargetMcSourceId(''); }}
                  className={clsx(
                    'flex flex-col items-center gap-2 rounded-xl border p-3 text-center transition-all duration-150',
                    isSelected
                      ? 'border-red-400 bg-red-50 ring-2 ring-red-300 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-red-200 hover:bg-red-50/40 hover:shadow-sm',
                  )}
                >
                  {agent.image_url ? (
                    <img src={agent.image_url} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow" />
                  ) : (
                    <div className={clsx('flex h-12 w-12 items-center justify-center rounded-full text-base font-bold ring-2 ring-white shadow', isSelected ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500')}>
                      {(agent.full_name ?? '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="w-full min-w-0">
                    <p className="truncate text-xs font-semibold leading-4 text-slate-800">{agent.full_name ?? 'Unknown'}</p>
                    <p className="mt-0.5 truncate text-[10px] text-slate-400">{agent.market_center_name ?? agent.source_market_center_id ?? '-'}</p>
                    <p className="mt-0.5 text-[11px] font-medium text-slate-500">{Number(agent.active_listing_count)} active listings</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedAgent && (
        <div className="surface-card space-y-4 p-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Step 2</p>
            <h3 className="mt-0.5 text-base font-semibold text-slate-800">Select target market centre</h3>
            <p className="mt-1 text-xs text-slate-500">
              Current market centre: <span className="font-semibold text-slate-700">{selectedAgent.market_center_name ?? selectedAgent.source_market_center_id ?? '-'}</span>
            </p>
          </div>

          <input
            value={targetSearch}
            onChange={(event) => setTargetSearch(event.target.value)}
            placeholder="Search target market centres..."
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />

          {marketCentersLoading ? (
            <p className="text-sm text-slate-500">Loading market centres...</p>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
              {visibleTargetMcs.map((mc) => {
                const isSelected = targetMcSourceId === mc.source_market_center_id;
                return (
                  <button
                    key={mc.source_market_center_id}
                    type="button"
                    onClick={() => setTargetMcSourceId(mc.source_market_center_id)}
                    className={clsx(
                      'w-full border-b border-slate-100 px-3 py-2.5 text-left text-sm transition-colors last:border-b-0',
                      isSelected ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    <p className="font-semibold">{mc.name}</p>
                    <p className="text-xs text-slate-500">{mc.source_market_center_id}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {canTransfer && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-5 py-4 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-slate-800">Ready to transfer agent</p>
            <p className="mt-0.5 text-xs text-slate-500">
              <span className="font-medium">{selectedAgent?.full_name}</span> from <span className="font-medium">{selectedAgent?.market_center_name ?? selectedAgent?.source_market_center_id}</span> to <span className="font-medium">{selectedTargetMc?.name}</span>
            </p>
          </div>
          <button type="button" onClick={() => setShowConfirm(true)} className="primary-btn">Transfer Agent</button>
        </div>
      )}

      {showConfirm && selectedAgent && selectedTargetMc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800">Confirm Agent Transfer</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Transfer <strong>{selectedAgent.full_name}</strong> from <strong>{selectedAgent.market_center_name ?? selectedAgent.source_market_center_id}</strong> to <strong>{selectedTargetMc.name}</strong>.
            </p>
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
              The associate profile and primary listings will move to the new market centre. Existing transactions keep their current historical market-centre context.
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setShowConfirm(false)} className="rounded-lg border border-slate-300 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={() => void startTransfer()} className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700">Yes, Transfer</button>
            </div>
          </div>
        </div>
      )}

      {showProgress && activeJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-xl space-y-4 rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Transferring Agent...</h3>
              {(activeJob.status === 'done' || activeJob.status === 'failed') && (
                <button type="button" onClick={() => setShowProgress(false)} className="text-xl leading-none text-slate-400 hover:text-slate-600">x</button>
              )}
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span>{activeJob.associateName}</span>
                <span>{activeJob.completedListings} / {activeJob.totalListings}</span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={clsx('h-full rounded-full transition-all duration-500', activeJob.status === 'done' ? 'bg-emerald-500' : activeJob.status === 'failed' ? 'bg-red-500' : 'bg-red-600')} style={{ width: `${progressPct}%` }} />
              </div>
            </div>
            {activeJob.status === 'running' || activeJob.status === 'pending' ? (
              <p className="animate-pulse text-sm text-slate-600">Processing transfer, please wait.</p>
            ) : activeJob.status === 'done' ? (
              <p className="text-sm font-medium text-emerald-700">Agent transfer complete.</p>
            ) : (
              <p className="text-sm font-medium text-red-700">Transfer failed: {activeJob.error}</p>
            )}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <p><span className="font-semibold">Listings updated:</span> {activeJob.listingsUpdated}</p>
              <p><span className="font-semibold">From:</span> {activeJob.fromMarketCenterName ?? activeJob.fromMarketCenterSourceId}</p>
              <p><span className="font-semibold">To:</span> {activeJob.toMarketCenterName ?? activeJob.toMarketCenterSourceId}</p>
            </div>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col space-y-4 rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex flex-shrink-0 items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Agent Transfer History</h3>
              <button type="button" onClick={() => setShowHistory(false)} className="text-xl text-slate-400 hover:text-slate-600">x</button>
            </div>
            {historyLoading && <p className="text-sm text-slate-500">Loading...</p>}
            {!historyLoading && historyRows.length === 0 && <p className="text-sm text-slate-500">No transfers recorded yet.</p>}
            {!historyLoading && historyRows.length > 0 && (
              <div className="flex-1 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 font-semibold uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2.5 text-left">Date</th>
                      <th className="px-3 py-2.5 text-left">Associate</th>
                      <th className="px-3 py-2.5 text-left">From MC</th>
                      <th className="px-3 py-2.5 text-left">To MC</th>
                      <th className="px-3 py-2.5 text-left">Listings Updated</th>
                      <th className="px-3 py-2.5 text-left">Done By</th>
                      <th className="px-3 py-2.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {historyRows.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap px-3 py-2 text-slate-500">{new Date(row.transferred_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}</td>
                        <td className="px-3 py-2 font-medium text-slate-700">{row.associate_name ?? row.associate_id}</td>
                        <td className="px-3 py-2 text-slate-600">{row.from_market_center_name ?? row.from_source_market_center_id ?? '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{row.to_market_center_name ?? row.to_source_market_center_id ?? '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{row.listings_updated}</td>
                        <td className="px-3 py-2 text-slate-500">{row.requested_by ?? '-'}</td>
                        <td className="px-3 py-2">{row.transfer_error ? <span className="font-medium text-red-600">Failed</span> : <span className="font-medium text-emerald-600">Success</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}